import { useState, useEffect, useRef, useCallback } from 'react';

// Constants
const THUMBNAIL_SIZE = 96;
const THUMBNAIL_METHOD = 'scale';
const FETCH_TIMEOUT = 10000; // 10 seconds

/**
 * Generates an authenticated Matrix media URL for a given MXC URL
 * @param {string} mxcUrl - The Matrix Content URL (mxc://...)
 * @param {Object} matrixClient - The Matrix client instance
 * @param {boolean} useThumbnail - Whether to generate a thumbnail URL
 * @returns {string|null} The authenticated HTTP URL or null if invalid
 */
export const getAuthenticatedImageUrl = (mxcUrl, matrixClient, useThumbnail = false) => {
    if (!mxcUrl || !matrixClient) return null;

    if (!mxcUrl.startsWith('mxc://')) {
        return null;
    }

    try {
        const mxcParts = mxcUrl.replace('mxc://', '').split('/');
        if (mxcParts.length < 2) {
            throw new Error('Invalid MXC URL structure');
        }

        const serverName = mxcParts[0];
        const mediaId = mxcParts[1];
        
        // Validate Matrix client methods
        const homeserverUrl = matrixClient.getHomeserverUrl();
        if (!homeserverUrl) {
            throw new Error('Unable to get homeserver URL');
        }
        
        // URL encode mediaId to handle special characters
        const encodedMediaId = encodeURIComponent(mediaId);

        // Generate media URL
        const url = useThumbnail
            ? `${homeserverUrl}/_matrix/media/v1/thumbnail/${serverName}/${encodedMediaId}?width=${THUMBNAIL_SIZE}&height=${THUMBNAIL_SIZE}&method=${THUMBNAIL_METHOD}`
            : `${homeserverUrl}/_matrix/client/v1/media/download/${serverName}/${encodedMediaId}`;

        return url;

    } catch (error) {
        return null;
    }
};

/**
 * Fetches an authenticated image from Matrix server and returns a blob URL
 * @param {string} mxcUrl - The Matrix Content URL
 * @param {Object} matrixClient - The Matrix client instance
 * @param {boolean} useThumbnail - Whether to fetch thumbnail or full image
 * @returns {Promise<Object>} Object containing url and cleanup function
 * @throws {Error} If URL generation or fetch fails
 */
// Track revoked URLs to make cleanup idempotent
const revokedUrls = new Set();

export const fetchAuthenticatedImage = async (mxcUrl, matrixClient, useThumbnail = false) => {
    const httpUrl = getAuthenticatedImageUrl(mxcUrl, matrixClient, useThumbnail);

    if (!httpUrl) {
        throw new Error('Could not generate media URL');
    }

    // Validate access token
    const accessToken = matrixClient.getAccessToken();
    if (!accessToken) {
        throw new Error('Unable to get access token');
    }

    let response;
    try {
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

        response = await fetch(httpUrl, {
            headers: {
                Authorization: `Bearer ${accessToken}`
            },
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            if (response.status === 404 && useThumbnail) {
                return fetchAuthenticatedImage(mxcUrl, matrixClient, false);
            }

            throw new Error(`Media fetch failed: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        
        // Validate blob
        if (!blob || blob.size === 0) {
            throw new Error('Invalid or empty image received');
        }
        
        const objectUrl = URL.createObjectURL(blob);

        return {
            url: objectUrl,
            cleanup: () => {
                // Idempotent cleanup - only revoke if not already revoked
                if (!revokedUrls.has(objectUrl)) {
                    try {
                        URL.revokeObjectURL(objectUrl);
                        revokedUrls.add(objectUrl);
                    } catch (error) {
                        console.warn('Error revoking object URL:', error);
                    }
                }
            }
        };
        
    } catch (error) {
        if (error.name === 'AbortError') {
            throw new Error('Image fetch timed out');
        }
        throw error;
    }
};

/**
 * React hook for managing authenticated images with automatic cleanup
 * @param {string} mxcUrl - The Matrix Content URL
 * @param {Object} matrixClient - The Matrix client instance
 * @returns {Object} Object containing url, loading, error, cleanup, and reload functions
 */
export const useAuthenticatedImage = (mxcUrl, matrixClient) => {
    const [imageData, setImageData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const cleanupRef = useRef(null);

    const loadImage = useCallback(async () => {
        if (!mxcUrl || !matrixClient) return;

        setLoading(true);
        setError(null);

        try {
            // Clean up previous URL if exists
            if (cleanupRef.current && typeof cleanupRef.current === 'function') {
                cleanupRef.current();
                cleanupRef.current = null;
            }

            const result = await fetchAuthenticatedImage(mxcUrl, matrixClient);
            setImageData(result.url);
            // Only assign cleanup if it's a valid function
            if (result.cleanup && typeof result.cleanup === 'function') {
                cleanupRef.current = result.cleanup;
            } else {
                cleanupRef.current = null;
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [mxcUrl, matrixClient, cleanupRef]);

    // Auto-cleanup on unmount
    useEffect(() => {
        return () => {
            if (cleanupRef.current && typeof cleanupRef.current === 'function') {
                try {
                    cleanupRef.current();
                } catch (error) {
                    console.warn('Error in authenticated image cleanup:', error);
                }
                cleanupRef.current = null;
            }
        };
    }, []);

    // Auto-load when dependencies change
    useEffect(() => {
        loadImage();
    }, [mxcUrl, matrixClient, loadImage]);

    const cleanup = () => {
        if (cleanupRef.current && typeof cleanupRef.current === 'function') {
            try {
                cleanupRef.current();
            } catch (error) {
                console.warn('Error in cleanup function:', error);
            }
            cleanupRef.current = null;
        }
        setImageData(null);
    };

    return { url: imageData, loading, error, cleanup, reload: loadImage };
};

